// Send an approved Meta template.
//
// This is the only message WhatsApp accepts outside the 24-hour window, so it
// serves both jobs: starting a conversation with someone who has never written
// in, and reopening one whose window has closed.
//
// Only APPROVED templates are selectable. A PENDING or REJECTED one is listed
// but disabled with its status shown, because "why is my template missing" is a
// worse question to be left with than "why is it greyed out".

import { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, RefreshCw, Send, X } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { customerService, type Customer } from '@/services/customer.service';
import {
  whatsappService,
  type WhatsAppTemplate,
} from '@/services/social-channels.service';

const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';
const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-[10px] border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center gap-[7px] rounded-lg bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const FIELD =
  'h-[34px] w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] ' +
  'px-3 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] ' +
  'focus:border-[var(--co-brass)] focus:outline-none';

interface PickableContact {
  key: string;
  name: string;
  /** Digits only - the form Meta and our session table both use. */
  phone: string;
  hint: string;
}

function normalizeDigits(value: string): string {
  return (value || '').replace(/\D/g, '');
}

function prettyDigits(digits: string): string {
  return digits ? `+${digits}` : '';
}

/** Substitutes {{1}}, {{2}} ... so the agent sees what the customer will get. */
export function renderPreview(body: string, parameters: string[]): string {
  return (body || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (match, index) => {
    const value = parameters[Number(index) - 1];
    return value?.trim() ? value : match;
  });
}

interface Props {
  /** Pre-filled and locked when reopening an existing conversation. */
  phone?: string;
  onClose: () => void;
  onSent: (phone: string) => void;
}

export default function TemplateSendDialog({ phone, onClose, onSent }: Props) {
  const toast = useToast();
  const locked = Boolean(phone);

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [to, setTo] = useState(phone || '');
  const [parameters, setParameters] = useState<string[]>([]);
  const [headerParameters, setHeaderParameters] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  // Contact picker - so a new conversation does not mean typing digits.
  const [contacts, setContacts] = useState<PickableContact[]>([]);
  const [contactQuery, setContactQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [pickedName, setPickedName] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await whatsappService.listTemplates();
      setTemplates(rows);
      setError(
        rows.length === 0
          ? 'No templates yet. Sync them from Meta, or create one in the Meta dashboard first.'
          : null,
      );
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load templates.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // People already talked to on WhatsApp, offered first - no typing needed.
  useEffect(() => {
    if (locked) return;
    whatsappService
      .listConversations()
      .then((rows) =>
        setContacts(
          rows.map((row) => ({
            key: `conv-${row.id}`,
            name: row.name || prettyDigits(row.phone),
            phone: row.phone,
            hint: 'Previous conversation',
          })),
        ),
      )
      .catch(() => {
        // The picker is a convenience; typing a number still works.
      });
  }, [locked]);

  // Customer records, searched on demand. Debounced so typing does not
  // fire a request per keystroke.
  useEffect(() => {
    if (locked) return undefined;
    const query = contactQuery.trim();
    if (query.length < 2) return undefined;

    setIsSearching(true);
    const timer = setTimeout(() => {
      customerService
        .getCustomers({ search: query, limit: 8 } as any)
        .then((page) => {
          const rows: Customer[] = (page as any)?.data || [];
          setContacts((current) => {
            const fromConversations = current.filter((c) => c.key.startsWith('conv-'));
            const fromCustomers = rows
              .filter((c) => c.phone && normalizeDigits(c.phone).length >= 7)
              .map((c) => ({
                key: `cust-${c.id}`,
                name: c.name || c.email || prettyDigits(normalizeDigits(c.phone || '')),
                phone: normalizeDigits(c.phone || ''),
                hint: 'Customer',
              }));
            // A number already in a conversation wins - it carries history.
            const seen = new Set(fromConversations.map((c) => c.phone));
            return [...fromConversations, ...fromCustomers.filter((c) => !seen.has(c.phone))];
          });
        })
        .catch(() => undefined)
        .finally(() => setIsSearching(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [contactQuery, locked]);

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId],
  );

  /** Previous conversations always show; customers appear once searched. */
  const visibleContacts = useMemo(() => {
    const query = contactQuery.trim().toLowerCase();
    if (!query) return contacts.filter((c) => c.key.startsWith('conv-')).slice(0, 6);
    return contacts
      .filter(
        (c) =>
          c.name.toLowerCase().includes(query) || c.phone.includes(query.replace(/\D/g, '')),
      )
      .slice(0, 8);
  }, [contacts, contactQuery]);

  // Resize the parameter inputs whenever the chosen template changes.
  useEffect(() => {
    setParameters(Array(selected?.parameterCount ?? 0).fill(''));
    setHeaderParameters(Array(selected?.headerParameterCount ?? 0).fill(''));
  }, [selected?.id, selected?.parameterCount, selected?.headerParameterCount]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await whatsappService.syncTemplates();
      if (!result.success) {
        setError(result.message);
      } else {
        setTemplates(result.templates);
        setError(result.templates.length === 0 ? 'Meta returned no templates.' : null);
        toast.success('Templates synced', result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not sync templates.');
    } finally {
      setIsSyncing(false);
    }
  };

  const digits = to.replace(/\D/g, '');
  const missingParameter =
    parameters.some((p) => !p.trim()) || headerParameters.some((p) => !p.trim());
  const canSend =
    Boolean(selected?.isSendable) && digits.length >= 7 && !missingParameter && !isSending;

  const handleSend = async () => {
    if (!selected || !canSend) return;
    setIsSending(true);
    try {
      const result = await whatsappService.sendTemplateMessage({
        to: digits,
        templateName: selected.name,
        languageCode: selected.language,
        parameters,
        headerParameters,
      });

      if (!result.success) {
        // Meta rejects with success:false, not an HTTP error.
        setError(result.message);
        return;
      }

      toast.success('Template sent', `Sent to +${digits}.`);
      onSent(digits);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not send the template.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className={clsx(CARD_CLASS, 'flex max-h-[85vh] w-full max-w-[560px] flex-col')}
        style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* header */}
        <div className="flex flex-none items-start justify-between gap-3 border-b border-[var(--co-line)] px-5 py-4">
          <div>
            <h2 className="text-[19px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              {locked ? 'Reopen with a template' : 'Start a new conversation'}
            </h2>
            <p className="mt-[6px] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
              WhatsApp only allows an approved template outside the 24-hour window.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex-none text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* body */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-[var(--co-line)] px-3 py-2">
              <AlertTriangle className="mt-[2px] h-[13px] w-[13px] flex-none text-[var(--co-terracotta)]" />
              <p className="text-[11.5px] leading-[1.5] text-[var(--co-ink-2)]">{error}</p>
            </div>
          )}

          <div>
            <label className="co-mono block text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              To
            </label>

            {!locked && (
              <>
                <input
                  type="text"
                  value={contactQuery}
                  onChange={(event) => setContactQuery(event.target.value)}
                  placeholder="Search a contact by name, email or number"
                  className={clsx(FIELD, 'mt-[7px]')}
                />

                {visibleContacts.length > 0 && (
                  <div className="mt-[7px] max-h-[150px] space-y-[4px] overflow-y-auto">
                    {visibleContacts.map((contact) => (
                      <button
                        key={contact.key}
                        type="button"
                        onClick={() => {
                          setTo(contact.phone);
                          setPickedName(contact.name);
                          setContactQuery('');
                        }}
                        className="flex w-full items-center justify-between gap-2 rounded-lg border px-[11px] py-[7px] text-left"
                        style={
                          to === contact.phone
                            ? { borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }
                            : { borderColor: 'var(--co-line)', background: 'var(--co-raised)' }
                        }
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[12.5px] font-semibold text-[var(--co-ink)]">
                            {contact.name}
                          </span>
                          <span className="co-mono block text-[10px] text-[var(--co-ink-3)]">
                            {prettyDigits(contact.phone)}
                          </span>
                        </span>
                        <span className="co-mono flex-none text-[9.5px] uppercase tracking-[.14em] text-[var(--co-ink-3)]">
                          {contact.hint}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {isSearching && (
                  <p className="mt-[5px] text-[11px] text-[var(--co-ink-3)]">Searching…</p>
                )}
              </>
            )}

            <input
              type="text"
              value={to}
              disabled={locked}
              onChange={(event) => {
                setTo(event.target.value);
                setPickedName(null);
              }}
              placeholder="or type a number, e.g. 919412607164"
              className={clsx(FIELD, 'mt-[7px] disabled:opacity-60')}
            />
            <p className="mt-[5px] text-[11px] text-[var(--co-ink-3)]">
              {pickedName
                ? `Sending to ${pickedName}.`
                : 'International format. On a Meta test number, this must be one of the allow-listed recipients.'}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Template
              </label>
              <button type="button" className={CONTROL} onClick={handleSync} disabled={isSyncing}>
                <RefreshCw className={clsx('h-[13px] w-[13px]', isSyncing && 'animate-spin')} />
                {isSyncing ? 'Syncing…' : 'Sync from Meta'}
              </button>
            </div>

            {isLoading ? (
              <p className="mt-[7px] text-[12.5px] text-[var(--co-ink-3)]">Loading…</p>
            ) : (
              <div className="mt-[7px] space-y-[6px]">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    disabled={!template.isSendable}
                    onClick={() => setSelectedId(template.id)}
                    className="flex w-full flex-col gap-[3px] rounded-lg border px-[11px] py-[9px] text-left disabled:cursor-not-allowed disabled:opacity-55"
                    style={
                      selectedId === template.id
                        ? { borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }
                        : { borderColor: 'var(--co-line)', background: 'var(--co-raised)' }
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[12.5px] font-semibold text-[var(--co-ink)]">
                        {template.name}
                      </span>
                      <span
                        className="co-mono flex-none text-[9.5px] uppercase tracking-[.14em]"
                        style={{
                          color: template.isSendable
                            ? 'var(--co-ink-3)'
                            : 'var(--co-terracotta)',
                        }}
                      >
                        {template.language} · {template.status}
                      </span>
                    </div>
                    <span className="line-clamp-2 text-[11.5px] leading-[1.5] text-[var(--co-ink-3)]">
                      {template.bodyText}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && selected.headerParameterCount > 0 && (
            <ParameterFields
              label="Header values"
              values={headerParameters}
              onChange={setHeaderParameters}
            />
          )}

          {selected && selected.parameterCount > 0 && (
            <ParameterFields
              label="Body values"
              values={parameters}
              onChange={setParameters}
            />
          )}

          {selected && (
            <div>
              <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                Preview
              </span>
              <div className="mt-[7px] rounded-[10px] border border-[var(--co-line)] px-3 py-2">
                {selected.headerText && (
                  <p className="mb-1 text-[12.5px] font-semibold text-[var(--co-ink)]">
                    {renderPreview(selected.headerText, headerParameters)}
                  </p>
                )}
                <p className="whitespace-pre-wrap text-[12.5px] leading-[1.55] text-[var(--co-ink)]">
                  {renderPreview(selected.bodyText, parameters)}
                </p>
                {selected.footerText && (
                  <p className="mt-1 text-[11px] text-[var(--co-ink-3)]">{selected.footerText}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* footer */}
        <div className="flex flex-none items-center justify-end gap-2 border-t border-[var(--co-line)] px-5 py-3">
          <button type="button" className={CONTROL} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={PRIMARY} disabled={!canSend} onClick={handleSend}>
            <Send className="h-[13px] w-[13px]" />
            {isSending ? 'Sending…' : 'Send template'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ParameterFields({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div>
      <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        {label}
      </span>
      <div className="mt-[7px] space-y-[6px]">
        {values.map((value, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="co-mono w-[34px] flex-none text-[11px] text-[var(--co-ink-3)]">
              {`{{${index + 1}}}`}
            </span>
            <input
              type="text"
              value={value}
              onChange={(event) => {
                const next = [...values];
                next[index] = event.target.value;
                onChange(next);
              }}
              placeholder={`Value for {{${index + 1}}}`}
              className={FIELD}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
